import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Upload, Link2, Archive, RotateCcw, Eye, Download, FileText, Lock, Plus, X, Trash2 } from "lucide-react";

const CATEGORIES = [
  ["handbook_policies", "Handbook & Policies"],
  ["moa_agreements", "MOA & Agreements"],
  ["forms_templates", "Forms & Templates"],
  ["meetings_training", "Consultant Meetings & Training"],
];
const VIS = [["all", "All consultants & Admin"], ["selected", "Selected consultants & Admin"], ["admin", "Admin only"]];

function UploadForm({ consultants, onSaved, onCancel }) {
  const [form, setForm] = useState({ title: "", category: "handbook_policies", description: "", visibility: "all",
    selected: [], version_label: "", effective_date: "", display_order: 0, external_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("category", form.category);
      fd.append("description", form.description); fd.append("visibility", form.visibility);
      fd.append("selected_consultant_ids", form.selected.join(","));
      fd.append("version_label", form.version_label); fd.append("effective_date", form.effective_date);
      fd.append("display_order", String(form.display_order)); fd.append("external_url", form.external_url);
      if (file) fd.append("file", file);
      await api.post("/documents", fd);
      toast.success("Document added.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card p-6 space-y-4 fade-up" data-testid="document-upload-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label">Title *</label>
          <input className="input" value={form.title} data-testid="doc-title-input"
            onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div><label className="label">Category</label>
          <select className="input" value={form.category} data-testid="doc-category-select"
            onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div className="sm:col-span-2"><label className="label">Description</label>
          <input className="input" value={form.description} data-testid="doc-description-input"
            onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div><label className="label">Visibility</label>
          <select className="input" value={form.visibility} data-testid="doc-visibility-select"
            onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
            {VIS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        {form.visibility === "selected" && (
          <div><label className="label">Visible to (consultants)</label>
            <div className="flex flex-wrap gap-1.5" data-testid="doc-selected-consultants">
              {consultants.map((c) => {
                const on = form.selected.includes(c.id);
                return <button key={c.id} type="button" data-testid={`doc-vis-${c.id}`}
                  onClick={() => setForm({ ...form, selected: on ? form.selected.filter((x) => x !== c.id) : [...form.selected, c.id] })}
                  className={`rounded-full px-3 py-1 text-xs font-bold border ${on ? "bg-navy text-white border-navy" : "bg-white border-[#E7E1D4]"}`}>
                  {c.name}</button>;
              })}
            </div></div>
        )}
        <div><label className="label">Version label</label>
          <input className="input" placeholder="e.g. v2026.1" value={form.version_label} data-testid="doc-version-input"
            onChange={(e) => setForm({ ...form, version_label: e.target.value })} /></div>
        <div><label className="label">Effective date</label>
          <input type="date" className="input" value={form.effective_date} data-testid="doc-effective-input"
            onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
        <div><label className="label">File (PDF, DOCX, PPTX, XLSX — PDF previews inline)</label>
          <input type="file" className="input !py-2 text-xs" onChange={(e) => setFile(e.target.files?.[0])} data-testid="doc-file-input" /></div>
        <div><label className="label">…or external link (Google Doc, authorized signing link, etc.)</label>
          <input className="input" placeholder="https://docs.google.com/…" value={form.external_url} data-testid="doc-url-input"
            onChange={(e) => setForm({ ...form, external_url: e.target.value })} /></div>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} disabled={busy || !form.title || (!file && !form.external_url)} data-testid="doc-save-button">
          {busy ? "Saving…" : "Add document"}</button>
        <button className="btn-ghost" onClick={onCancel} data-testid="doc-cancel-button">Cancel</button>
      </div>
    </div>
  );
}

function DocRow({ doc, isAdmin, consultants, onChange }) {
  const [preview, setPreview] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`;
  const isPdf = (doc.content_type || "").includes("pdf");

  const archive = async () => {
    await api.patch(`/documents/${doc.id}`, { archive: true });
    onChange(); toast.success("Archived. Linked history is preserved.");
  };
  const replace = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/documents/${doc.id}/replace`, fd);
      onChange(); toast.success("New version saved. The prior version is archived for Admin reference.");
    } catch (e) { toast.error(apiError(e)); }
    setReplacing(false);
  };

  return (
    <div className="rounded-lg border border-[#E7E1D4] bg-white p-4" data-testid={`doc-row-${doc.id}`}>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <FileText size={17} className="text-terracotta shrink-0" />
          <div className="min-w-0">
            <div className="font-bold text-navy text-sm flex items-center gap-2 flex-wrap">
              {doc.title}
              {doc.version_label && <span className="badge-neutral">{doc.version_label}</span>}
              {doc.visibility !== "all" && <span className="badge-draft"><Lock size={10} /> {doc.visibility === "admin" ? "Admin only" : "Selected consultants"}</span>}
              {doc.is_sample && <span className="badge-neutral">Sample</span>}
            </div>
            {doc.description && <div className="text-xs text-[#78716C] mt-0.5">{doc.description}</div>}
            <div className="text-[11px] text-[#A8A29E] mt-0.5">
              {doc.effective_date && `Effective ${doc.effective_date} · `}Added by {doc.uploaded_by}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {doc.has_file && isPdf && (
            <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setPreview(!preview)} data-testid={`doc-preview-${doc.id}`}>
              <Eye size={13} /> Preview</button>)}
          {doc.has_file && (
            <a href={fileUrl} target="_blank" rel="noreferrer" className="btn-ghost !py-1 !px-2 text-xs" data-testid={`doc-download-${doc.id}`}>
              <Download size={13} /> Download</a>)}
          {doc.external_url && (
            <a href={doc.external_url} target="_blank" rel="noreferrer" className="btn-ghost !py-1 !px-2 text-xs" data-testid={`doc-link-${doc.id}`}>
              <Link2 size={13} /> Open link</a>)}
          {isAdmin && (
            <>
              <label className="btn-ghost !py-1 !px-2 text-xs cursor-pointer" data-testid={`doc-replace-${doc.id}`}>
                <RotateCcw size={13} /> Replace
                <input type="file" className="hidden" onChange={(e) => replace(e.target.files?.[0])} />
              </label>
              <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" onClick={archive} data-testid={`doc-archive-${doc.id}`}>
                <Archive size={13} /> Archive</button>
              <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" data-testid={`doc-delete-${doc.id}`}
                title="Delete document"
                onClick={async () => {
                  if (!window.confirm(`Permanently delete "${doc.title}"? This cannot be undone.`)) return;
                  try { await api.delete(`/documents/${doc.id}`); onChange(); toast.success("Document deleted."); }
                  catch (e) { toast.error(apiError(e)); }
                }}>
                <Trash2 size={13} /> Delete</button>
            </>
          )}
        </div>
      </div>
      {preview && isPdf && (
        <iframe src={fileUrl} className="w-full h-[480px] mt-3 rounded-lg border border-[#E7E1D4]" title={doc.title}
          data-testid={`doc-preview-frame-${doc.id}`} />)}
    </div>
  );
}

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [cat, setCat] = useState("");

  const load = () => api.get(`/documents${cat ? `?category=${cat}` : ""}`).then((r) => setDocs(r.data));
  useEffect(() => { load(); }, [cat]);
  useEffect(() => { if (isAdmin) api.get("/admin/consultants").then((r) => setConsultants(r.data)); }, []);

  if (!docs) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="documents-loading">Loading documents…</div>;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Company Documents</p>
          <h1 className="section-title text-3xl mt-1">Agreements, Policies & Resources</h1>
          <p className="mt-3 text-sm text-[#57534E] max-w-2xl">
            {isAdmin ? "Upload files or links, set visibility, versions, and effective dates. Replacing a document archives the prior version for Admin reference."
                      : "View and download the company documents shared with you. The shared library is managed by Admin."}
          </p>
        </div>
        {isAdmin && <button className="btn-primary" onClick={() => setShowUpload(!showUpload)} data-testid="add-document-button">
          {showUpload ? <X size={15} /> : <Plus size={15} />} Add document</button>}
      </div>

      {showUpload && isAdmin && <UploadForm consultants={consultants} onSaved={() => { setShowUpload(false); load(); }} onCancel={() => setShowUpload(false)} />}

      <div className="flex flex-wrap gap-2 fade-up-1" data-testid="doc-category-filters">
        <button onClick={() => setCat("")} data-testid="doc-filter-all"
          className={`rounded-full px-4 py-1.5 text-xs font-bold border ${!cat ? "bg-navy text-white border-navy" : "bg-white border-[#E7E1D4]"}`}>All</button>
        {CATEGORIES.map(([k, l]) => (
          <button key={k} onClick={() => setCat(k)} data-testid={`doc-filter-${k}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold border ${cat === k ? "bg-navy text-white border-navy" : "bg-white border-[#E7E1D4]"}`}>{l}</button>
        ))}
      </div>

      {CATEGORIES.filter(([k]) => !cat || cat === k).map(([k, label]) => {
        const items = docs.filter((d) => d.category === k);
        if (!items.length && cat) return null;
        return (
          <div key={k} className="space-y-3 fade-up-2" data-testid={`doc-category-${k}`}>
            <h2 className="font-serif font-bold text-lg text-navy">{label} <span className="text-xs font-sans font-normal text-[#78716C]">({items.length})</span></h2>
            {items.length === 0
              ? <p className="text-sm text-[#78716C]">Nothing here yet{isAdmin ? " — add the first document above." : "."}</p>
              : <div className="space-y-3">{items.map((d) => <DocRow key={d.id} doc={d} isAdmin={isAdmin} consultants={consultants} onChange={load} />)}</div>}
          </div>
        );
      })}
    </div>
  );
}
